const FEED_CACHE_KEY = "feedPageCache_v4";
const DISCOVERY_CACHE_KEY = "feedDiscoveryCache_v2";
const CACHE_TTL_MS = 2 * 60 * 1000;
const LS_ADDRESS_COORDS = "userAddressCoords";
const LS_FEED_SORT = "tasteFeedSort";

const inflight = new Map();

const readEntry = (storageKey) => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const { savedAt, data } = JSON.parse(raw);
    if (data == null || !savedAt || Date.now() - savedAt > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
};

const writeEntry = (storageKey, data) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify({ savedAt: Date.now(), data }));
  } catch {
    /* quota or private mode */
  }
};

const dedupe = (key, run) => {
  const existing = inflight.get(key);
  if (existing) return existing;
  const promise = Promise.resolve()
    .then(run)
    .finally(() => {
      if (inflight.get(key) === promise) inflight.delete(key);
    });
  inflight.set(key, promise);
  return promise;
};

export function readFeedSort() {
  try {
    const saved = localStorage.getItem(LS_FEED_SORT);
    if (saved === "popular") return "recent";
    if (saved === "discovery" || saved === "nearby" || saved === "recent" || saved === "following") {
      return saved;
    }
  } catch {
    /* ignore */
  }
  return "recent";
}

export function readFeedCoords() {
  try {
    const raw = localStorage.getItem(LS_ADDRESS_COORDS);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const lat = Number(parsed?.lat);
    const lng = Number(parsed?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  } catch {
    return null;
  }
}

function roundedCoord(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  return number.toFixed(2);
}

export function feedCacheId({ userId, sort, coords } = {}) {
  return `${userId || "guest"}|${sort || "recent"}|${roundedCoord(coords?.lat)}|${roundedCoord(coords?.lng)}`;
}

export function buildFeedParams(sort, coords) {
  const params = { limit: 20 };
  if (sort === "following") {
    params.scope = "following";
  } else if (sort === "nearby" && coords) {
    params.scope = "nearby";
    params.maxDistance = 10000;
  } else if (sort === "recent") {
    params.scope = "recent";
  } else if (coords) {
    params.scope = "nearby";
    params.maxDistance = 10000;
  }
  if (coords) {
    params.lat = coords.lat;
    params.lng = coords.lng;
  }
  return params;
}

function normalizeFeedPayload(body) {
  const data = body?.data || {};
  const tastes = Array.isArray(data.posts) ? data.posts : Array.isArray(data.tastes) ? data.tastes : [];
  return {
    tastes,
    restaurants: Array.isArray(data.restaurants) ? data.restaurants : [],
    items: Array.isArray(data.items) ? data.items : [],
    scoped: data.scoped || "",
    cursor: data.nextCursor || null,
    hasMore: Boolean(data.hasMore),
  };
}

export function loadFeedCache(identity) {
  const data = readEntry(`${FEED_CACHE_KEY}:${feedCacheId(identity)}`);
  if (!data || typeof data !== "object" || !Array.isArray(data.tastes)) return null;
  return data;
}

export function saveFeedCache(identity, payload) {
  writeEntry(`${FEED_CACHE_KEY}:${feedCacheId(identity)}`, payload);
}

export function fetchFeedFirstPage(api, identity) {
  const sort = identity?.sort || "recent";
  const coords = identity?.coords || null;
  return dedupe(`feed:${feedCacheId({ ...identity, sort, coords })}`, async () => {
    const res = await api.get("/api/feed", { params: buildFeedParams(sort, coords) });
    const data = normalizeFeedPayload(res.data);
    saveFeedCache({ ...identity, sort, coords }, data);
    return data;
  });
}

export function discoveryCacheId({ coords, category = "all", sort = "total", maxKm = "" } = {}) {
  return `${category}|${sort}|${maxKm}|${roundedCoord(coords?.lat)}|${roundedCoord(coords?.lng)}`;
}

export function loadDiscoveryCache(identity) {
  const data = readEntry(`${DISCOVERY_CACHE_KEY}:${discoveryCacheId(identity)}`);
  if (!data || typeof data !== "object") return null;
  return data;
}

export function saveDiscoveryCache(identity, payload) {
  writeEntry(`${DISCOVERY_CACHE_KEY}:${discoveryCacheId(identity)}`, payload);
}

export function fetchDiscoveryFirstPage(api, identity) {
  const coords = identity?.coords || null;
  const category = identity?.category || "all";
  const sort = identity?.sort || "total";
  const maxKm = identity?.maxKm;
  return dedupe(`discovery:${discoveryCacheId(identity)}`, async () => {
    const params = { limit: 12, category, sort };
    if (coords) {
      params.lat = coords.lat;
      params.lng = coords.lng;
    }
    if (maxKm != null && maxKm !== "") params.maxKm = maxKm;
    const res = await api.get("/api/feed/discovery", { params });
    const payload = res.data?.data || {};
    saveDiscoveryCache(identity, payload);
    return payload;
  });
}

/** Warm the feed the user last had open, before they tap the tab. */
export function prefetchFeedPageData(api, { userId, sort, coords } = {}) {
  const nextSort = sort || readFeedSort();
  const nextCoords = coords === undefined ? readFeedCoords() : coords;
  if (nextSort === "discovery") {
    if (loadDiscoveryCache({ coords: nextCoords })) return Promise.resolve();
    return fetchDiscoveryFirstPage(api, { coords: nextCoords }).catch(() => {});
  }
  const identity = { userId, sort: nextSort, coords: nextCoords };
  if (loadFeedCache(identity)) return Promise.resolve();
  return fetchFeedFirstPage(api, identity).catch(() => {});
}
